import { useMemo } from 'react';

type LockFn = <Args extends unknown[]>(
  fn: (...args: Args) => void,
  label: string,
) => (...args: Args) => void;

interface UseTopBarHandlersDeps {
  lockedWhilePreview: LockFn;
  // Direct handlers from various function-hooks
  handleSave: () => void;
  handleSaveAs: () => void;
  handleSaveToCloud: () => void;
  handleLoad: () => void;
  handleExportPNG: () => void;
  handleSimulate: () => void;
  handleClearRequest: () => void;
  handleCopyJson: () => Promise<void> | void;
  // Dialog openers
  openNewWorkflow: () => void;
  openImport: () => void;
  openHelp: () => void;
  openAiJson: () => void;
  openAiAssistant: () => void;
  openOverview: () => void;
  openCloudWorkspace: () => void;
  openShareWorkspace: () => void;
  openLogin: () => void;
  openRepoUpload: () => void;
}


/**
 * Bag:ar alla 14 lockedWhilePreview-wrappade handlers + 3 oprotokade till
 * en stable callback-bag som TopBar-JSX kan spreada. Tidigare låg dessa
 * inline i `<TopBar>`-elementet med 14 individuella `lockedWhilePreview()`-
 * anrop som spammade JSX:en med string-labels.
 *
 * useMemo:ar för stable references — TopBar är inte React.memo idag men
 * vi vill inte uppgradera hookens behavior om det blir det imorgon.
 */
export function useTopBarHandlers(deps: UseTopBarHandlersDeps) {
  const {
    lockedWhilePreview,
    handleSave, handleSaveAs, handleSaveToCloud,
    handleLoad, handleExportPNG, handleSimulate, handleClearRequest, handleCopyJson,
    openNewWorkflow, openImport, openHelp, openAiJson, openAiAssistant,
    openOverview, openCloudWorkspace, openShareWorkspace, openLogin, openRepoUpload,
  } = deps;

  return useMemo(() => ({
    onSave: lockedWhilePreview(handleSave, 'Spara'),
    onSaveAs: lockedWhilePreview(handleSaveAs, 'Spara som'),
    onSaveToCloud: lockedWhilePreview(handleSaveToCloud, 'Spara till molnet'),
    onNewFlow: lockedWhilePreview(openNewWorkflow, 'Nytt flöde'),
    onLoad: lockedWhilePreview(handleLoad, 'Ladda'),
    onRepoUpload: lockedWhilePreview(openRepoUpload, 'Ladda upp repository'),
    onExportPNG: lockedWhilePreview(handleExportPNG, 'Exportera PNG'),
    onSimulate: lockedWhilePreview(handleSimulate, 'Simulera'),
    onClear: lockedWhilePreview(handleClearRequest, 'Rensa canvas'),
    onImportJson: lockedWhilePreview(openImport, 'Importera JSON'),
    onCopyJson: lockedWhilePreview(handleCopyJson as () => void, 'Kopiera JSON'),
    onAiJson: lockedWhilePreview(openAiJson, 'AI JSON-dialog'),
    onAiAssistant: lockedWhilePreview(openAiAssistant, 'AI-assistent'),
    onPreview: lockedWhilePreview(openOverview, 'Preview'),
    onCloudWorkspace: lockedWhilePreview(openCloudWorkspace, 'Cloud Workspace'),
    onShareWorkspace: lockedWhilePreview(openShareWorkspace, 'Dela workspace'),
    // Unprotected (dialogs that work even during preview):
    onHelp: openHelp,
    onLogin: openLogin,
  }), [
    lockedWhilePreview,
    handleSave, handleSaveAs, handleSaveToCloud,
    handleLoad, handleExportPNG, handleSimulate, handleClearRequest, handleCopyJson,
    openNewWorkflow, openImport, openHelp, openAiJson, openAiAssistant,
    openOverview, openCloudWorkspace, openShareWorkspace, openLogin, openRepoUpload,
  ]);
}

